/**
 * @fileoverview Business event query/fetch functions.
 * @module logic/businessEvents/businessEventsQueries
 */

import {
  fetchUserRoleData,
  getAuthJsonHeaders,
  logDevError,
  resolveExpiredToken,
} from "../../utils/eventApi.js";
import { filterBusinessStyleEventsForUserList } from "../../utils/eventDateFilters.js";
import {
  canViewPastEvents,
  filterNonCancelledEvents,
  sortEventsFuturesFirst,
} from "../../utils/pastEventsAccess.js";
import { getUser } from "../user";

const DATE_FORMAT_YYYY_MM_DD = "YYYY-MM-DD";

/**
 * Fetches business events available to a user based on membership type and date filters.
 * Eligible Personalizar users also receive past non-cancelled events (futures first).
 * @async
 * @param {string} userId - User identifier
 * @param {Object} user - User object with tipoDeUsuario property
 * @param {Function} navigate - React Router navigation function
 * @param {object|null|undefined} [permissions]
 * @returns {Promise<Array>} Sorted array of available business events
 */
export const getBusinessEvents = async (
  userId,
  user,
  navigate,
  permissions,
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/user/${userId}`,
      { method: "GET", headers: getAuthJsonHeaders() },
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getBusinessEvents(userId, user, navigate, permissions),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    if (Array.isArray(data)) {
      const includePast = canViewPastEvents(user, permissions);
      const currentDate = new Date();
      const availableEvents = filterNonCancelledEvents(
        filterBusinessStyleEventsForUserList(data, user, currentDate, {
          includePast,
        }),
      );
      return sortEventsFuturesFirst(availableEvents, currentDate);
    }
    return [];
  } catch (error) {
    logDevError(error);
    return [];
  }
};

/**
 * Fetches all active business events from the API.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of active business event objects
 */
export const getActiveBusinessEvents = async (navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/active`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getActiveBusinessEvents(navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    if (Array.isArray(data)) {
      return filterNonCancelledEvents(data);
    }
    return [];
  } catch (error) {
    logDevError(error);
    return [];
  }
};

/**
 * Fetches all business events without date filtering (for name validation).
 * @async
 * @param {string} userId - User identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of all business events
 */
export const getAllBusinessEvents = async (userId, navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/user/${userId}`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getAllBusinessEvents(userId, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    if (Array.isArray(data)) {
      return data;
    } else {
      return [];
    }
  } catch (error) {
    logDevError(error);
    return [];
  }
};

/**
 * Fetches all business events by year.
 * @async
 * @param {string} year - Year to filter events by
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of business events by year
 */
export const getBusinessEventsByYear = async (year, navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/year/${year}`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getBusinessEventsByYear(year, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    if (Array.isArray(data)) {
      return data;
    } else {
      return [];
    }
  } catch (error) {
    logDevError(error);
    return [];
  }
};

/**
 * Fetches initial page data including user roles, user data, and business events.
 * @async
 * @param {string} userId - User identifier
 * @param {Function} changePermissions - Callback to update permissions state
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setUser - State setter for user data
 * @returns {Promise<Array>} Array of business events
 */
export const getData = async (userId, changePermissions, navigate, setUser) => {
  try {
    const permissions = await fetchUserRoleData(
      userId,
      changePermissions,
      navigate,
    );
    const userData = await getUser(userId);
    setUser(userData);
    return await getBusinessEvents(userId, userData, navigate, permissions);
  } catch (error) {
    logDevError(error);
    return [];
  }
};

/**
 * Fetches event details and formats dates for form display.
 * @async
 * @param {string} eventId - Business event identifier
 * @param {Function} navigate - React Router navigation function
 * @param {Object} dayjs - Day.js library instance
 * @param {Function} setFormData - State setter for form data
 * @returns {Promise<void>}
 */
export const getEventDetails = async (
  eventId,
  navigate,
  dayjs,
  setFormData,
) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/${eventId}`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, async () => {
      await getEventDetails(eventId, navigate, dayjs, setFormData);
    });
    if (tokenResult?.redirected) {
      return;
    }
    if (tokenResult) {
      return;
    }

    setFormData({
      ...data,
      fechaEvento: data.fechaEvento
        ? dayjs(data.fechaEvento).utc().format(DATE_FORMAT_YYYY_MM_DD)
        : null,
      fechaDisponibleSocios: data.fechaDisponibleSocios
        ? dayjs(data.fechaDisponibleSocios).utc().format(DATE_FORMAT_YYYY_MM_DD)
        : null,
      fechaDisponibleNoSocios: data.fechaDisponibleNoSocios
        ? dayjs(data.fechaDisponibleNoSocios)
            .utc()
            .format(DATE_FORMAT_YYYY_MM_DD)
        : null,
      fechaVigenciaCompradores: data.fechaVigenciaCompradores
        ? dayjs(data.fechaVigenciaCompradores)
            .utc()
            .format(DATE_FORMAT_YYYY_MM_DD)
        : null,
      fechaLimiteRegistro: data.fechaLimiteRegistro
        ? dayjs(data.fechaLimiteRegistro).utc().format(DATE_FORMAT_YYYY_MM_DD)
        : null,
    });
  } catch (error) {
    logDevError(error);
  }
};

/**
 * Fetches business events for a specific buyer user.
 * @async
 * @param {string} buyerUserId - Buyer user identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of business events for the buyer
 */
export const getBusinessEventsForBuyer = async (buyerUserId, navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/buyer/${buyerUserId}`,
      options,
    );
    const data = await response.json();

    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getBusinessEventsForBuyer(buyerUserId, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }

    if (data?.status === "success" && Array.isArray(data.data)) {
      return data.data;
    } else {
      return [];
    }
  } catch (error) {
    logDevError("Error fetching business events for buyer:", error);
    return [];
  }
};
